import { builder } from './builder';
import { units } from './constants';

export const contentDiscoveryConfig = builder.holder({
  useContentDiscovery: builder.boolean('use_content_discovery', {
    defaultValue: false,
    label: 'Enable content discovery',
    description:
      'Display the content discovery and disabled the recommended links. When the user close the Ogury browser without navigating, he will see multiple images'
  }),
  title: builder.string('content_discovery_title', {
    label: 'Title',
    description: 'Title of the content discovery',
    if: 'use_content_discovery'
  }),
  hasImage: builder.exists('content_discovery_image_1', {
    defaultValue: false
  }),
  imageUrl1: builder.string('content_discovery_image_1', {
    label: 'Card 1 : Image',
    description: 'Image of the first card of the content discovery',
    overrideType: 'image',
    if: 'use_content_discovery'
  }),
  text1: builder.string('content_discovery_text_1', {
    label: 'Card 1 : Text',
    description: 'Text of the first card of the content discovery',
    if: 'use_content_discovery'
  }),
  noBounceUrl1: builder.string('content_discovery_nobounce_1', {
    label: 'Card 1 : Target Url',
    description: 'Redirect url used when the user clicked on the first card',
    if: 'use_content_discovery'
  }),
  imageUrl2: builder.string('content_discovery_image_2', {
    if: 'use_content_discovery',
    optional: true,
    label: 'Card 2 : Image',
    description: 'Image of the second card of the content discovery',
    overrideType: 'image'
  }),
  text2: builder.string('content_discovery_text_2', {
    if: 'use_content_discovery',
    optional: true,
    label: 'Card 2 : Text',
    description: 'Text of the second card of the content discovery'
  }),
  noBounceUrl2: builder.string('content_discovery_nobounce_2', {
    if: 'use_content_discovery',
    optional: true,
    label: 'Card 2 : Target Url',
    description: 'Redirect url used when the user clicked on the second card'
  }),
  imageUrl3: builder.string('content_discovery_image_3', {
    if: 'use_content_discovery',
    optional: true,
    label: 'Card 3 : Image',
    description: 'Image of the third card of the content discovery',
    overrideType: 'image'
  }),
  text3: builder.string('content_discovery_text_3', {
    if: 'use_content_discovery',
    optional: true,
    label: 'Card 3 : Text',
    description: 'Text of the third card of the content discovery'
  }),
  noBounceUrl3: builder.string('content_discovery_nobounce_3', {
    if: 'use_content_discovery',
    optional: true,
    label: 'Card 3 : Target Url',
    description: 'Redirect url used when the user clicked on the third card'
  }),
  autoCloseDuration: builder.number('content_discovery_acd', {
    optional: true,
    // Warning if you change the default value, it is also set in a getParameter function in browser-sdk3/index.js
    defaultValue: 5,
    label: 'Autoclose duration',
    description: 'Autoclose duration in second of the content discovery',
    if: 'use_content_discovery',
    unit: units.seconds
  }),
  displayCloseButton: builder.boolean(
    'content_discovery_display_close_button',
    {
      // Warning if you change the default value, this parameter is forced to be true in browser-sdk3/index.js
      defaultValue: true,
      label: 'Display Close Button',
      description: 'Display close button or not',
      if: 'use_content_discovery'
    }
  ),
  useLoader: builder.boolean('content_discovery_use_loader', {
    label: 'Display loader on click',
    defaultValue: false,
    description:
      'When the user clicks on a card of the content discovery, we display a fullscreen loader component'
  }),
  redirectUrl: builder.string('content_discovery_redirect_url', {
    label: 'Redirect URL before nobounce',
    defaultValue: '',
    description: 'Redirect to this page before redirect to nobounce url'
  }),
  loaderTimeout: builder.number('content_discovery_loader_timeout', {
    label: 'Duration before closing the loader (in seconds)',
    defaultValue: 5,
    description: 'Maximum display time of the loader',
    unit: units.seconds
  }),
  loaderCloseUrlRegex: builder.string(
    'content_discovery_loader_close_url_regex',
    {
      label: "Regex of the url that close the loader (defaultValue: '')",
      defaultValue: '',
      description:
        'Regex of the url that close the loader: when a page is loaded in the browser and matchs this regex, the loader is automatically closed'
    }
  ),
  loaderImage: builder.string('content_discovery_loader_image', {
    label: 'Loader image',
    defaultValue: '',
    description: 'Loader image'
  }),
  loaderColor: builder.string('content_discovery_loader_color', {
    label: 'Loader color',
    defaultValue: '#ef3834',
    description: 'Loader color'
  }),
  triggerUrlRegex: builder.string('content_discovery_trigger_url_regex', {
    label: 'Trigger URL regex',
    defaultValue: ''
  }),
  useFullscreen: builder.boolean('content_discovery_use_fullscreen', {
    label: 'Fullscreen Enabled',
    defaultValue: false
  }),
  closeBtnSize: builder.number('content_discovery_close_btn_size', {
    label: 'Close button size',
    defaultValue: 20,
    unit: units.pixel
  }),
  closeBtnColor: builder.string('content_discovery_close_btn_color', {
    label: 'Close btn color',
    defaultValue: '#575757'
  }),
  useCountdownCloseButton: builder.boolean(
    'content_discovery_use_countdown_close_button',
    {
      if: 'use_content_discovery',
      defaultValue: true,
      label: 'Countdown close button',
      description: 'Allow the close button to show a countdown'
    }
  ),
  closeButtonDelay: builder.number('content_discovery_close_button_delay', {
    if: 'use_content_discovery',
    defaultValue: 3,
    label: 'Close button delay',
    description: 'Delay in second before showing the close button',
    unit: units.seconds
  }),
  customTranslations: builder.object('content_discovery_translations', {
    defaultValue: {},
    description: `Custom translations in JSON format.<br>Overridable fields: \`title\` and \`content_discovery_loading\`<br>ex: \`{"en": {"content_discovery_loading": "Loading..."}}\``
  }),
  objectFit: builder.string('content_discovery_object_fit', {
    possibleValues: [
      {
        name: 'Cover',
        value: 'cover'
      },
      {
        name: 'Contain',
        value: 'contain'
      }
    ],
    defaultValue: 'contain'
  }),
  blurEffect: builder.holder({
    show: builder.boolean('content_discovery_blur_effect_show', {
      defaultValue: true,
      label: 'Card Background Blur effect',
      description: 'Display blur background behind the cards'
    }),

    zoom: builder.number('content_discovery_blur_effect_zoom', {
      defaultValue: 0,
      label: 'Card Background Blur effect Zoom',
      description:
        'Zoom of the blur background<br>0 to infinit : 0 is 100%, 5 is 150%, 10 is 200% of the background size...'
    }),

    offsetX: builder.number('content_discovery_blur_effect_offsetX', {
      defaultValue: 0,
      label:
        'Card Background Blur effect X position (landscape orientation only)',
      description:
        'X position of the blur background<br>positive or negative pixel number, allow to move the background horizontally<br>landscape only',
      unit: units.pixel
    }),

    offsetY: builder.number('content_discovery_blur_effect_offsetY', {
      defaultValue: 0,
      label:
        'Card Background Blur effect Y position (portrait orientation only)',
      description:
        'Y position of the blur background<br>positive or negative pixel number, allow to move the background vertically<br>landscape only',
      unit: units.pixel
    })
  })
});
